import { Injectable } from '@angular/core';

import { GroupItemType } from '@cds/betting-offer';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { ModularConfigAccessorService } from '@frontend/sports/widget/core/feature';

import { TabbedGridTabBarItem } from './tabbed-grid-navigation.service';
import { TabbedChevronClickTrackingData } from './tabbed-grid.model';

@Injectable()
export class TabbedGridTrackingService {
    private active?: TabbedGridTabBarItem;

    constructor(
        private trackingService: TrackingService,
        private accessorService: ModularConfigAccessorService,
    ) {}

    getTrackingValueForMatchList(item: TabbedGridTabBarItem): string {
        this.active = item;

        switch (item.group?.type) {
            case GroupItemType.Sport:
                return `sport/${item.itemId}`;
            case GroupItemType.Competition:
                return `competition/${item.itemId}`;
            default:
                return `affiliation/${item.itemId}`;
        }
    }

    trackTabSelected(item: TabbedGridTabBarItem, index: number): void {
        this.active = item;

        const trackingObject: Record<string, any> = {
            ...this.getPartialTrackingObject(),
            ...this.accessorService.getWidget()?.trackingData,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: item.title,
            [trackingConstants.COMPONENT_POSITION_EVENT]: item.group?.type?.toString(),
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getTrackingLocationByType(),
            [trackingConstants.COMPONENT_CONTENT_POSITION]: index.toString(),
        };

        switch (item.group?.type) {
            case GroupItemType.Sport:
                trackingObject[trackingConstants.SPORT_ID] = item.itemId.toString();
                break;
            case GroupItemType.Competition:
                trackingObject[trackingConstants.SPORT_LEAGUE_ID] = item.itemId.toString();
                break;
            default:
                trackingObject[trackingConstants.COMPONENT_POSITION_EVENT] = 'Affiliation';
                trackingObject[trackingConstants.COMPONENT_EVENT_DETAILS] = item.itemId.toString();
                break;
        }
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingObject);
    }

    trackLinkClick(details: TabbedChevronClickTrackingData): void {
        const trackingObject = {
            ...this.getPartialTrackingObject(),
            ...this.accessorService.getWidget()?.trackingData,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: details.eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: details.urlClicked,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getTrackingLocationByType(),
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingObject);
    }

    trackEventClick(event: EventModel, index: number): void {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            ...this.getPartialTrackingObject(),
            ...this.accessorService.getWidget()?.trackingData,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: index.toString(),
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getTrackingLocationByType() ?? NOT_APPLICABLE,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'sport event',
            [trackingConstants.SPORT_EVENT_ID]: event.id,
            [trackingConstants.SPORT_ID]: event.sport.id.toString(),
        });
    }

    private getTrackingLocationByType(): string | undefined {
        if (this.active) {
            switch (this.active.group?.type) {
                case GroupItemType.Sport:
                    return this.getTrackingLocationEvent(this.active.group.type, this.active.itemId);
                case GroupItemType.Competition:
                    return this.getTrackingLocationEvent(this.active.group.type, this.active.itemId);
                default:
                    return this.getTrackingLocationEvent('Affiliation', this.active.itemId);
            }
        }

        return undefined;
    }

    private getTrackingLocationEvent(type: string, id: string | number): string {
        return [this.accessorService.getPage(), this.accessorService.getWidget()?.templateName, type, id]
            .filter((segment) => segment)
            .join('/')
            .toLowerCase();
    }

    private getPartialTrackingObject(): { [key: string]: string } {
        return {
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'sport events',
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: `${this.accessorService.getPage()?.toLowerCase()} grid widgets`,
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'Click',
        };
    }
}
